import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ConsumerChargePersonalInterFace,
  RequestConsumerChargePersonalInterFace,
} from '../../../../interface/end-consumer-charge-session-personal-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setSelectedDevice } from '../../../../redux/slices/DeviceManagementSlice';
import {
  fetchConsumerChargePersonal,
  fetchConsumerChargePersonalFilter,
  setSortData,
  updateConsumerChargePersonalListFilter,
} from '../../../../redux/slices/EndConsumerChargePersonal';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ChargingSessionSidePanel from '../../../../shared-components/table-side-panel/ChargingSessionSidePanel';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { convertUtcTo12HourTime } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';
import ActiveSession from '../SidePaneComponents/ActiveChargingSession/ActiveChargingSession';

interface ModelDetailsInterface {
  title?: string;
  content: JSX.Element;
  url?: string;
  urlNewTab?: string;
}

/**
 * Charge Session List
 * @returns {any} - The ChargeSessionPersonalList component
 */
function ChargeSessionPersonalList() {
  const { t, i18n } = useTranslation('endConsumer');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    content: <div />,
    url: '',
    urlNewTab: '',
  });
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    consumers,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.ConsumerChargePersonal);
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);
  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestConsumerChargePersonalInterFace) => {
    if (!loading && selectedEndConsumer) {
      dispatch(
        fetchConsumerChargePersonal({
          userCustomId: selectedEndConsumer.userCustomId,
          // status: 'completed',
          type: 'personal',
          ...data,
        }),
      );
    }
  };

  /**
   * Handle open end consumer details
   * @param {any} rowData - The rowData
   */
  const handleOpenDeviceDetails = (rowData: ConsumerChargePersonalInterFace) => {
    navigate(`?deviceId=${rowData?.chargingPoint?.chargingPointSn}`);
    setIsOpen(true);
    dispatch(
      setSelectedDevice({
        id: rowData?.chargingPoint?._id,
        name: rowData?.chargingPoint?.chargingPointName,
        chargingPointSn: rowData?.chargingPoint?.chargingPointSn,
      }),
    );
    setModelDetails({
      title: '',
      content: <DeviceSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/charger/${rowData?.chargingPoint?._id}/device-details?searchBy=${rowData?.chargingPoint?.chargingPointName}`,
      urlNewTab: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/charger-expanded/${rowData?.chargingPoint?._id}/device-details?searchBy=${rowData?.chargingPoint?.chargingPointName}`,
    });
  };
  /**
   * Handle open active session details
   * @param {any} currentRow - The currentRow
   */
  const handleOpenActiveSessionDetails = (currentRow: ConsumerChargePersonalInterFace) => {
    setIsOpen(true);
    setModelDetails({
      title: 'Live Session',
      content: <ActiveSession dataList={currentRow} consumerType="personal" />,
    });
  };

  /**
   * Handle open charge session details
   * @param {any} rowData - The rowData
   */
  const handleOpenChargeSessionDetails = (rowData: ConsumerChargePersonalInterFace) => {
    navigate(`?chargingId=${rowData?._id}`);
    setIsOpen(true);
    setModelDetails({
      title: t('chargingSessionDetails'),
      content: <ChargingSessionSidePanel fetchIdFrom="queryParams" datatype="personal" />,
    });
  };

  const column = [
    {
      label: t('startDateTime'),
      accessorKey: 'transactionStartsAt' as keyof ConsumerChargePersonalInterFace,
      sortAllow: true,
      /**
       * Custom data function
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: ConsumerChargePersonalInterFace) => (
        <span>{ConvertUTCtoDateTime(currentRow?.transactionStartsAt)}</span>
      ),
    },
    {
      label: t('endDateTime'),
      accessorKey: 'transactionEndsAt' as keyof ConsumerChargePersonalInterFace,
      sortAllow: true,
      /**
       * Custom data function
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: ConsumerChargePersonalInterFace) => (
        <span>{ConvertUTCtoDateTime(currentRow?.transactionEndsAt)}</span>
      ),
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof ConsumerChargePersonalInterFace,
      sortAllow: false,
      /**
       * Rendering device name with hyper link
       * @param {any} currentRow - Current row data
       * @returns {any} - Device name cell
       */
      customData: (currentRow: ConsumerChargePersonalInterFace) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenDeviceDetails(currentRow)}>
          {currentRow?.chargingPoint?.chargingPointSn || ''}
        </p>
      ),
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumedInKwh' as keyof ConsumerChargePersonalInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: ConsumerChargePersonalInterFace) => {
        return (
          <p role="presentation">
            {ToFixedValue(Number(currentRow?.powerConsumedInKwh), 3)} {t('kWh')}
          </p>
        );
      },
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof ConsumerChargePersonalInterFace,
      sortAllow: false,
      /**
       * Status
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: ConsumerChargePersonalInterFace) => {
        if (currentRow?.status === 'inProgress') {
          return (
            <p
              role="presentation"
              className="hyper-link"
              onClick={() => handleOpenActiveSessionDetails(currentRow)}>
              {t('inProgress')}
            </p>
          );
        }
        return (
          <p
            role="presentation"
            className="hyper-link"
            onClick={() => handleOpenChargeSessionDetails(currentRow)}>
            {currentRow?.status ? t(currentRow.status) : t('na')}
          </p>
        );
      },
    },
  ];

  /**
   * Current columns for Personal Charging Session export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('startDateTime'), accessorKey: 'transactionStartsAt' },
    { label: t('endDateTime'), accessorKey: 'transactionEndsAt' },
    { label: t('device'), accessorKey: 'chargingPointSn' }, // ✅ FIXED
    { label: t('powerConsumed'), accessorKey: 'powerConsumedInKwh' },
    { label: t('status'), accessorKey: 'status' },
  ];
  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );
  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = { ...queryParameters };

    payload.type = 'personal';

    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;

    if (!selectedEndConsumer?.userCustomId) return;

    endConsumerApi
      .chargingSessionExport(selectedEndConsumer.userCustomId, payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: ConsumerChargePersonalInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'transactionStartsAt':
                return row?.transactionStartsAt
                  ? `${ConvertUTCtoDate(
                      row.transactionStartsAt,
                    )} ${convertUtcTo12HourTime(row.transactionStartsAt)}`
                  : t('na');

              case 'transactionEndsAt':
                return row?.transactionEndsAt
                  ? `${ConvertUTCtoDate(
                      row.transactionEndsAt,
                    )} ${convertUtcTo12HourTime(row.transactionEndsAt)}`
                  : t('na');

              case 'chargingPointSn':
                return row?.chargingPoint?.chargingPointSn || t('na');

              case 'status':
                return row?.status ? t(row.status) : t('na');

              case 'powerConsumedInKwh':
                return row?.powerConsumedInKwh
                  ? `${ToFixedValue(Number(row.powerConsumedInKwh), 3)} ${t('kWh')}`
                  : t('na');

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'end-consumer-charging-session-personal.csv');
          } else {
            exportPDF(headers, rows, 'end-consumer-charging-session-personal.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param {any} value - Pagination limit value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const currentId = selectedEndConsumer.userCustomId;
    const lastId = sessionStorage.getItem('chargeSessionPersonal');

    if (isFilterUpdated || consumers.length === 0 || lastId !== currentId) {
      if (selectedEndConsumer?.userCustomId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        });
      }
    }
    sessionStorage.setItem('chargeSessionPersonal', currentId);
  }, [filters, selectedEndConsumer]);

  return (
    <div className="charging-session-wrap" dir={i18n.dir()}>
      <SidePaneModel
        open={isOpen}
        title={modelDetails.title}
        content={modelDetails.content}
        closePanel={handleCloseModal}
        url={modelDetails.url}
        urlNewTab={modelDetails.urlNewTab}
      />
      <CustomTable
        header={column}
        data={consumers}
        serialNo
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={500}
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateConsumerChargePersonalListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConsumerChargePersonalFilter}
        exportData={consumers}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        requiredRefreshToggle
        refershCurrentPages={handleRefresh}
        refreshFunction={handleRefresh}
        refreshInterval={10}
        hoverLabel={t('refresh')}
        leftTopContent={<> </>}
        expandTableOpen
        expandTableOpenKey="ChargeSessionPersonalV2ListEndConsumer"
        onHiddenColumnsChange={setHiddenColumns}
      />{' '}
    </div>
  );
}

export default ChargeSessionPersonalList;
